// @ts-strict-ignore
import { useCallback, useMemo, useRef, useState } from 'react';

import { Video, useShareLinksQuery } from 'api';
import { FormattedMessage } from 'react-intl';

import Divider from 'components/divider';
import Modal, { ModalType } from 'components/modal';
import { TalkActionButton } from 'components/pages/dubbing/TalkActionButton';
import { useTalkPageContext } from 'components/pages/dubbing/slug/talk-page-context';
import { Heading, Microcopy } from 'components/typography';
import useVideoPlayerStore from 'components/video-player-dubbing/store';

import { Text } from 'components/@tedui';
import { useShortURL } from '../../hooks';
import CopyLink from './CopyLink';
import DownloadLink from './DownloadLink';
import ShareLink from './ShareLink';

type ShareProps = {
  slug: string;
  title: Video['title'];
  presenters: Video['presenterDisplayName'];
  context: string;
};

const getEmbedCode = ({
  langCode = 'NONE',
  size = {
    x: 854,
    y: 480
  },
  slug
}) => {
  return [
    `<div style="max-width:${size.x}px">`,
    '<div style="position:relative;height:0;padding-bottom:56.25%">',
    '<iframe ',
    `src="https://embed.ted.com/talks/${
      langCode && langCode !== 'NONE' ? `lang/${langCode}/` : ''
    }${slug}" `,
    `width="${size.x}" `,
    `height="${size.y}" `,
    'style="position:absolute;left:0;top:0;width:100%;height:100%" ',
    'frameborder="0" scrolling="no" ',
    'allowfullscreen></iframe>',
    '</div>',
    '</div>'
  ].join('');
};

const Share = ({
  ref,
  slug,
  title,
  presenters,
  context
}: ShareProps & {
  ref: React.RefObject<HTMLDivElement | null>;
}) => {
  const { onTalkShareStart, dubbingLang } = useVideoPlayerStore(state => ({
    onTalkShareStart: state.onTalkShareStart,
    dubbingLang: state.dubbingLang
  }));
  const [modalOpen, setModalOpen] = useState(false);
  const { language } = useTalkPageContext();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const shareUrl = useMemo(() => {
    if (global.location === undefined) return '';
    return `${global.location.href}&utm_campaign=tedspread&utm_medium=referral&utm_source=tedcomshare`;
  }, [slug, dubbingLang]);

  const { shortURL } = useShortURL({
    url: shareUrl
  });

  const { error, data } = useShareLinksQuery({
    variables: { slug, language },
    ssr: false
  });

  const video = data?.videos?.nodes?.[0];

  const native = video?.nativeDownloads;
  const audioDownload = video?.audioDownload;
  const nativeDownload = !!native && native.medium;

  const shareMessage = `${title} (${presenters} | ${context})`;

  const embedCode = useMemo(
    () => getEmbedCode({ slug, langCode: language }),
    [slug, language]
  );

  const sendEvent = useCallback(
    label => {
      onTalkShareStart(label);
    },
    [onTalkShareStart]
  );

  if (error) {
    throw error;
  }

  return (
    <div ref={ref}>
      <TalkActionButton
        ref={buttonRef}
        iconName="send"
        onClick={() => setModalOpen(true)}
        label="Share"
      />

      <Modal
        type={ModalType.Medium}
        isOpen={modalOpen}
        onDismiss={() => setModalOpen(false)}
        ariaLabel="Share this talk"
        noOverflow
        stickyCloseButton
        maxWidth
      >
        <Heading size="s" className="mb-3 pt-12">
          <FormattedMessage defaultMessage="Share this talk" />
        </Heading>
        <Divider className="mb-3 w-full" />

        <div className="flex md:justify-between">
          <div className="share">
            <Microcopy size="m" className="text-gray-700">
              <FormattedMessage defaultMessage="Share" />
            </Microcopy>

            <div className="flex flex-wrap">
              <ShareLink
                service="facebook"
                message={shareMessage}
                url={shareUrl}
              />

              <ShareLink
                service="linkedin"
                message={shareMessage}
                url={shareUrl}
              />
              <ShareLink
                service="twitter"
                message={shareMessage}
                url={shortURL || shareUrl}
              />
              <ShareLink
                service="sms"
                message={encodeURIComponent(shareMessage)}
                url={shortURL || shareUrl}
                mobile
              />

              <ShareLink
                service="whatsapp"
                message={encodeURIComponent(shareMessage)}
                url={shortURL || shareUrl}
                mobile
              />

              <ShareLink
                service="messenger"
                message={shareMessage}
                url={shortURL || shareUrl}
                mobile
              />

              <ShareLink
                service="mail"
                message={encodeURIComponent(shareMessage)}
                url={shortURL || shareUrl}
              />
            </div>
          </div>
          {dubbingLang === 'en' && (
            <div className="download md:ml-10">
              <Microcopy size="m" className="mb-2 text-gray-700">
                <FormattedMessage defaultMessage="Download" />
              </Microcopy>

              <div className="flex">
                {nativeDownload && (
                  <DownloadLink
                    href={nativeDownload}
                    iconName="video"
                    downloadFormat="video"
                  >
                    <FormattedMessage defaultMessage="MP4" />
                  </DownloadLink>
                )}
                <DownloadLink
                  href={audioDownload}
                  iconName="headphones"
                  downloadFormat="audio"
                >
                  <FormattedMessage defaultMessage="Audio" />
                </DownloadLink>
              </div>
            </div>
          )}
        </div>
        <div className="mb-8 mt-7">
          <Text variant="body2">
            <FormattedMessage defaultMessage="Link" />
          </Text>
          <div className="relative mb-1 w-full">
            <CopyLink
              service="url"
              url={shareUrl}
              onCopy={() => sendEvent('link')}
            />
          </div>
          <div className="relative mb-8 w-full">
            <Text variant="body2">
              <FormattedMessage defaultMessage="Embed" />
            </Text>
            <div className="relative mb-1 w-full">
              <CopyLink
                service="embed"
                url={embedCode}
                onCopy={() => sendEvent('embed')}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Share;
